import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ChatItem } from "@/components/MessageBubble";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { dayStartMs, localDayStartMs } from "./dayHelpers";
import {
  DEFAULT_DEMO_SCENARIO,
  DEMO_SCENARIO_LIST,
  DEMO_SCENARIOS,
  persistDemoScenario,
  readStoredDemoScenario,
} from "./demo-scenarios";
import type { DemoLibraryData, DemoScenarioId, DemoScenarioMeta, DemoWeightPoint } from "./demo-scenarios";

type DemoFoodEntry = Doc<"foodEntries">;
type DemoExerciseEntry = Doc<"exerciseEntries">;
type DemoMemory = Doc<"foodMemories">;
type DemoUser = Doc<"users">;
export type DemoWeight = DemoWeightPoint;
export type { DemoScenarioId, DemoScenarioMeta } from "@/lib/demo-scenarios";

export type DemoDashboard = {
  readonly goalKcal: number | null;
  readonly goalProteinG: number | null;
  readonly goalCarbsG: number | null;
  readonly goalFatG: number | null;
  readonly goalFiberG: number | null;
  readonly consumedKcal: number;
  readonly consumedProteinG: number | null;
  readonly consumedCarbsG: number | null;
  readonly consumedFatG: number | null;
  readonly consumedFiberG: number | null;
  readonly burnedKcal: number;
  readonly netKcal: number;
  readonly remainingKcal: number;
  readonly foodEntries: readonly DemoFoodEntry[];
  readonly exerciseEntries: readonly DemoExerciseEntry[];
};

export const DEMO_ANCHOR_MS = dayStartMs(Date.UTC(2026, 6, 17));
export const DEMO_MIN_OFFSET = -21;
export const DEMO_MAX_OFFSET = 20;

type DemoContextValue = {
  readonly demoMode: boolean;
  readonly anchorDate: number;
  readonly installUrl: string | null;
  readonly scenario: DemoScenarioId;
  readonly scenarios: readonly DemoScenarioMeta[];
  readonly setScenario: (id: DemoScenarioId) => void;
  readonly profile: DemoUser;
  readonly preferences: { alwaysAsk: boolean; autoMemory: boolean; webSearch: boolean };
  readonly memories: readonly DemoMemory[];
  readonly chatItems: readonly ChatItem[];
  readonly library: DemoLibraryData;
  readonly getSummary: (date: number) => DemoDashboard;
  readonly getRange: (start: number, end: number) => { date: number; consumedKcal: number; burnedKcal: number; netKcal: number; entryCount: number }[];
  readonly getWeights: (start: number, end: number) => readonly DemoWeight[];
  readonly upsertWeight: (args: { date: number; weightKg: number }) => void;
  readonly addMeal: (args: { date: number; name: string; kcal: number; proteinG?: number; carbsG?: number; fatG?: number; fiberG?: number; notes?: string }) => void;
  readonly addExercise: (args: { date: number; type: string; durationMin: number; calories: number; notes?: string }) => void;
  readonly updateFood: (id: Id<"foodEntries">, patch: Partial<Pick<DemoFoodEntry, "name" | "finalKcal" | "notes" | "proteinG" | "carbsG" | "fatG" | "fiberG">>) => void;
  readonly removeFood: (id: Id<"foodEntries">) => void;
  readonly updateExercise: (id: Id<"exerciseEntries">, patch: Partial<Pick<DemoExerciseEntry, "type" | "finalBurnedKcal" | "notes">>) => void;
  readonly removeExercise: (id: Id<"exerciseEntries">) => void;
  readonly appendChat: (prompt: string) => void;
  readonly resetChat: () => void;
  readonly disableDemo: () => void;
  readonly enableDemo: (installUrl?: string | null) => void;
};

const DemoContext = createContext<DemoContextValue | null>(null);

function demoId<Table extends "foodEntries" | "exerciseEntries">(value: string): Id<Table> {
  return value as Id<Table>;
}

export function DemoProvider({ enabled, installUrl, onDisable, onEnable, children }: { enabled: boolean; installUrl: string | null; onDisable: () => void; onEnable: (installUrl?: string | null) => void; children: ReactNode }) {
  const anchorDate = useMemo(() => DEMO_ANCHOR_MS, []);
  const [scenario, setScenarioState] = useState<DemoScenarioId>(readStoredDemoScenario);
  const definition = DEMO_SCENARIOS[scenario];
  const seed = useMemo(() => ({
    entries: definition.createEntries(anchorDate),
    weights: definition.createWeights(anchorDate),
    memories: definition.createMemories(anchorDate),
    chat: definition.createChat(),
  }), [definition, anchorDate]);
  const [foods, setFoods] = useState(seed.entries.foods);
  const [exercises, setExercises] = useState(seed.entries.exercises);
  const [memories, setMemories] = useState(seed.memories);
  const [weights, setWeights] = useState<DemoWeight[]>(seed.weights);
  const [chatItems, setChatItems] = useState<ChatItem[]>(seed.chat);
  const profile = useMemo(() => definition.createUser(anchorDate), [definition, anchorDate]);
  const nextId = useRef(0);

  // Re-seed all local demo state when the scenario changes (render-phase
  // adjustment pattern — React discards this render and re-renders with the
  // fresh seeds before anything commits).
  const [seededScenario, setSeededScenario] = useState(scenario);
  if (seededScenario !== scenario) {
    console.log("[Frontend][Demo] Re-seeding local demo state for scenario", { from: seededScenario, to: scenario });
    setSeededScenario(scenario);
    setFoods(seed.entries.foods);
    setExercises(seed.entries.exercises);
    setMemories(seed.memories);
    setWeights(seed.weights);
    setChatItems(seed.chat);
  }

  const setScenario = useCallback((id: DemoScenarioId) => {
    setScenarioState((current) => {
      if (current !== id) console.log("[Frontend][Demo] Scenario switched", { from: current, to: id });
      return id;
    });
    persistDemoScenario(id);
  }, []);

  const getSummary = useCallback((date: number): DemoDashboard => {
    const day = dayStartMs(date);
    const dayFoods = foods.filter((entry) => entry.date === day);
    const dayExercises = exercises.filter((entry) => entry.date === day);
    const sum = (field: "proteinG" | "carbsG" | "fatG" | "fiberG") => {
      const values = dayFoods.flatMap((entry) => {
        const value = entry[field];
        return value === undefined ? [] : [value];
      });
      return values.length === 0 ? null : values.reduce((total, value) => total + value, 0);
    };
    const consumedKcal = dayFoods.reduce((total, entry) => total + (entry.finalKcal ?? entry.estimatedKcal ?? 0), 0);
    const burnedKcal = dayExercises.reduce((total, entry) => total + (entry.finalBurnedKcal ?? entry.estimatedBurnedKcal), 0);
    return { goalKcal: profile.dailyGoalKcal ?? null, goalProteinG: profile.dailyProteinG ?? null, goalCarbsG: profile.dailyCarbsG ?? null, goalFatG: profile.dailyFatG ?? null, goalFiberG: profile.dailyFiberG ?? null, consumedKcal, consumedProteinG: sum("proteinG"), consumedCarbsG: sum("carbsG"), consumedFatG: sum("fatG"), consumedFiberG: sum("fiberG"), burnedKcal, netKcal: consumedKcal - burnedKcal, remainingKcal: (profile.dailyGoalKcal ?? 0) - consumedKcal + burnedKcal, foodEntries: dayFoods, exerciseEntries: dayExercises };
  }, [exercises, foods, profile]);

  const getRange = useCallback((start: number, end: number) => {
    const rows: { date: number; consumedKcal: number; burnedKcal: number; netKcal: number; entryCount: number }[] = [];
    for (let date = dayStartMs(start); date <= dayStartMs(end); date += 86_400_000) {
      const summary = getSummary(date);
      rows.push({ date, consumedKcal: summary.consumedKcal, burnedKcal: summary.burnedKcal, netKcal: summary.netKcal, entryCount: summary.foodEntries.length + summary.exerciseEntries.length });
    }
    return rows;
  }, [getSummary]);

  const getWeights = useCallback((start: number, end: number) => weights.filter((weight) => weight.date >= dayStartMs(start) && weight.date <= dayStartMs(end)), [weights]);

  const upsertWeight = useCallback(({ date, weightKg }: { date: number; weightKg: number }) => {
    const normalizedDate = localDayStartMs(date);
    setWeights((current) => {
      const existing = current.some((weight) => weight.date === normalizedDate);
      if (existing) return current.map((weight) => weight.date === normalizedDate ? { date: normalizedDate, weightKg } : weight);
      return [...current, { date: normalizedDate, weightKg }].sort((left, right) => left.date - right.date);
    });
  }, []);

  const addMeal = useCallback((args: { date: number; name: string; kcal: number; proteinG?: number; carbsG?: number; fatG?: number; fiberG?: number; notes?: string }) => {
    const id = nextId.current;
    nextId.current += 1;
    const loggedAt = Date.now();
    setFoods((current) => [...current, { _id: demoId(`demo-food-manual-${id}`), _creationTime: loggedAt, userId: "demo-user" as Id<"users">, date: localDayStartMs(args.date), name: args.name, estimatedKcal: args.kcal, finalKcal: args.kcal, proteinG: args.proteinG, carbsG: args.carbsG, fatG: args.fatG, fiberG: args.fiberG, source: "manual", status: "logged", notes: args.notes, loggedAt }]);
  }, []);

  const addExercise = useCallback((args: { date: number; type: string; durationMin: number; calories: number; notes?: string }) => {
    const id = nextId.current;
    nextId.current += 1;
    const loggedAt = Date.now();
    setExercises((current) => [...current, { _id: demoId(`demo-exercise-manual-${id}`), _creationTime: loggedAt, userId: "demo-user" as Id<"users">, date: localDayStartMs(args.date), type: args.type, durationMin: args.durationMin, estimatedBurnedKcal: args.calories, finalBurnedKcal: args.calories, source: "manual", status: "logged", notes: args.notes, loggedAt }]);
  }, []);

  const appendChat = useCallback((prompt: string) => {
    const reply = definition.chatReply;
    setChatItems((current) => {
      const nextIndex = current.length;
      return [...current, { kind: "text", role: "user", text: prompt, messageId: `demo-user-${nextIndex}`, messageIndex: nextIndex }, { kind: "text", role: "assistant", text: reply, messageId: `demo-assistant-${nextIndex}`, messageIndex: nextIndex + 1 }];
    });
  }, [definition]);

  const resetChat = useCallback(() => {
    console.log("[Frontend][Demo] Chat reset to scenario seed", { scenario });
    setChatItems(definition.createChat());
  }, [definition, scenario]);

  const value = useMemo<DemoContextValue>(() => ({
    demoMode: enabled,
    anchorDate,
    installUrl,
    scenario,
    scenarios: DEMO_SCENARIO_LIST,
    setScenario,
    profile,
    preferences: { alwaysAsk: true, autoMemory: true, webSearch: true },
    memories,
    chatItems,
    library: definition.library,
    getSummary,
    getRange,
    getWeights,
    upsertWeight,
    addMeal,
    addExercise,
    updateFood: (id, patch) => setFoods((current) => current.map((entry) => entry._id === id ? { ...entry, ...patch } : entry)),
    removeFood: (id) => setFoods((current) => current.filter((entry) => entry._id !== id)),
    updateExercise: (id, patch) => setExercises((current) => current.map((entry) => entry._id === id ? { ...entry, ...patch } : entry)),
    removeExercise: (id) => setExercises((current) => current.filter((entry) => entry._id !== id)),
    appendChat,
    resetChat,
    disableDemo: onDisable,
    enableDemo: onEnable,
  }), [addExercise, addMeal, anchorDate, appendChat, definition, enabled, getRange, getSummary, getWeights, installUrl, memories, onDisable, onEnable, profile, resetChat, scenario, setScenario, chatItems, upsertWeight]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemoMode(): DemoContextValue {
  const context = useContext(DemoContext);
  if (context !== null) return context;
  const fallback = DEMO_SCENARIOS[DEFAULT_DEMO_SCENARIO];
  return {
    demoMode: false,
    anchorDate: DEMO_ANCHOR_MS,
    installUrl: null,
    scenario: DEFAULT_DEMO_SCENARIO,
    scenarios: DEMO_SCENARIO_LIST,
    setScenario: () => undefined,
    profile: fallback.createUser(DEMO_ANCHOR_MS),
    preferences: { alwaysAsk: true, autoMemory: true, webSearch: true },
    memories: [],
    chatItems: [],
    library: fallback.library,
    getSummary: () => ({
      goalKcal: null,
      goalProteinG: null,
      goalCarbsG: null,
      goalFatG: null,
      goalFiberG: null,
      consumedKcal: 0,
      consumedProteinG: null,
      consumedCarbsG: null,
      consumedFatG: null,
      consumedFiberG: null,
      burnedKcal: 0,
      netKcal: 0,
      remainingKcal: 0,
      foodEntries: [],
      exerciseEntries: [],
    }),
    getRange: () => [],
    getWeights: () => [],
    upsertWeight: () => undefined,
    addMeal: () => undefined,
    addExercise: () => undefined,
    updateFood: () => undefined,
    removeFood: () => undefined,
    updateExercise: () => undefined,
    removeExercise: () => undefined,
    appendChat: () => undefined,
    resetChat: () => undefined,
    disableDemo: () => undefined,
    enableDemo: () => undefined,
  };
}
